'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import WelcomeHeader from './WelcomeHeader';
import CourseProgressCard from './CourseProgressCard';
import UpcomingClassCard from './UpcomingClassCard';
import AchievementBadge from './AchievementBadge';
import QuickAccessCard from './QuickAccessCard';

interface Course {
  id: string;
  title: string;
  instructor: string;
  progress: number;
  image: string;
  alt: string;
  nextClass: string;
  totalLessons: number;
  completedLessons: number;
}

interface UpcomingClass {
  id: string;
  courseTitle: string;
  batchName?: string;
  instructor: string;
  date: string;
  time: string;
  duration: string;
  isLive: boolean;
  zoomLink?: string;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  earned: boolean;
  earnedDate?: string;
}

interface QuickAccess {
  id: string;
  title: string;
  description: string;
  icon: string;
  href: string;
  color: string;
}

interface DashboardInteractiveProps {
  userName: string;
  currentStreak: number;
  totalPoints: number;
  courses: Course[];
  upcomingClasses: UpcomingClass[];
  achievements: Achievement[];
  learningPaths?: any[];
  peerConnections?: any[];
  quickAccessItems: QuickAccess[];
  scheduleItems?: any[];
}

export default function DashboardInteractive({
  userName,
  currentStreak,
  totalPoints,
  courses,
  upcomingClasses,
  achievements,
  quickAccessItems
}: DashboardInteractiveProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="w-full pt-24 pb-16 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg p-6 lg:p-8 h-32 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const hasEnrolledCourses = courses && courses.length > 0;

  return (
    <div className="w-full pt-24 pb-16 px-4 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto space-y-10">
        {/* Welcome Header */}
        <WelcomeHeader
          userName={userName}
          currentStreak={currentStreak}
          totalPoints={totalPoints}
        />

        {/* Enrolled Courses Section */}
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-900">
              My Enrolled Courses
            </h2>
            <Link
              href="/course-catalog"
              className="text-blue-600 font-medium hover:text-blue-700 transition-colors duration-200 text-base"
            >
              Browse All Courses →
            </Link>
          </div>
          
          {!hasEnrolledCourses ? (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
              <div className="max-w-md mx-auto space-y-4">
                <svg 
                  className="mx-auto h-20 w-20 text-muted-foreground" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" 
                  />
                </svg>
                <h3 className="text-2xl font-headline font-bold text-foreground">
                  No Enrolled Courses Yet
                </h3>
                <p className="text-muted-foreground text-lg">
                  Start your learning journey by selecting a course and completing payment to access course materials and live classes.
                </p>
                <Link
                  href="/course-catalog"
                  className="inline-block px-8 py-4 bg-primary text-primary-foreground rounded-xl font-cta font-bold hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl text-lg"
                >
                  Select Courses
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <CourseProgressCard key={course.id} {...course} />
              ))}
            </div>
          )}
        </section>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Upcoming Classes & Quick Access */}
          <div className="lg:col-span-2 space-y-10">
            {/* Upcoming Live Classes */}
            <section className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                Upcoming Live Classes
              </h2>
              
              {!hasEnrolledCourses ? (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-10 text-center">
                  <p className="text-muted-foreground text-lg">
                    Enroll in courses to see your upcoming live classes here.
                  </p>
                </div>
              ) : upcomingClasses && upcomingClasses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {upcomingClasses.map((classItem) => (
                    <UpcomingClassCard key={classItem.id} {...classItem} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-10 text-center">
                  <p className="text-muted-foreground text-lg">
                    No upcoming classes scheduled at the moment. Check back later for new batch schedules.
                  </p>
                </div>
              )}
            </section>

            {/* Quick Access Section */}
            <section className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                Quick Access
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {quickAccessItems.map((item) => (
                  <QuickAccessCard key={item.id} {...item} />
                ))}
              </div>
            </section>
          </div>

          {/* Right Column - Achievements */}
          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Achievements
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {achievements.map((achievement) => (
                  <AchievementBadge key={achievement.id} {...achievement} />
                ))}
              </div>
              <button className="w-full mt-6 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-300 font-medium text-gray-700 text-base">
                View All Achievements
              </button>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}